// Copyright ©️ 2026 eVolve MEP, LLC

import type { GridApi, IRowNode } from 'ag-grid-enterprise';

/**
 * Refreshes a server-side grid while keeping the data visible until refresh is finished.
 *
 * If the grid uses row-grouping, refreshes each group one at a time starting with the `node`'s group (if provided).
 * */
export const refreshGrid = <TData>(
  api: GridApi<TData> | null,
  /** If refreshing grouped data, MAKE SURE to pass this in */
  node?: IRowNode<TData>,
) => {
  if (!api || api.isDestroyed()) return;

  // Note that this will only properly traverse one-level-deep groups
  // (which, at the time of writing, is all we support on server side grids)
  const nodeGroup = node?.parent?.id;
  const routes = api
    .getServerSideGroupLevelState()
    .map(({ route }) => route)
    .filter((route) => route.length > 0)
    // Refresh the group with our node in it first, such that if our node is no longer in this group,
    // it is removed before being loaded by another group
    .toSorted((a, b) => (nodeGroup ? (a.includes(nodeGroup) ? -1 : b.includes(nodeGroup) ? 1 : 0) : 0));

  const onStoreRefreshed = () => {
    if (api.isDestroyed()) return;

    const nextRoute = routes.shift();
    if (nextRoute) {
      api.refreshServerSide({ route: nextRoute });
    } else {
      api.removeEventListener('storeRefreshed', onStoreRefreshed);
    }
  };
  api.addEventListener('storeRefreshed', onStoreRefreshed);

  api.refreshServerSide({ route: routes.shift() });
};
